import type {
  ConditionDesign,
  DesignerNodeData,
  EffectDesign,
  ResponseDesign,
  TriggerConditionBuild,
  TriggerDesign,
} from "../schemas/index.ts";
import {
  composeReferenceVariable,
  decomposeReferenceVariable,
  isDirectReferenceVariable,
  repointNodeValueRefs,
  stripUnsupportedTranslationsCreate,
  transformNodeSubtree,
  traverseNodeSubtree,
} from "../shared/index.ts";
import type { GetNodeTypeIdById } from "../state/index.ts";
import type { VariableConfig } from "../types/index.ts";
import type { NodeImportLookup } from "./nodeImportLookup.ts";
import type { GetNewId } from "./shared.ts";

export interface GenerateDesignerNodeForImportCreateOptions {
  getNextId: GetNewId;
  supportedLanguageCodes: Readonly<string[]>;
  getNodeTypeId: GetNodeTypeIdById;
  lookup: NodeImportLookup;
}

export interface GenerateDesignerNodeForImportOptions {
  dereferencedNodesByOriginalId?: Map<number, DesignerNodeData>;
}

// TODO: validation step that traverses checking version with getManifest on typeId
export const generateDesignerNodeForImportCreate = ({
  getNextId,
  supportedLanguageCodes,
  lookup: { getNewNodeId, hasNewNodeId },
}: GenerateDesignerNodeForImportCreateOptions) => {
  const stripUnsupportedTranslations = stripUnsupportedTranslationsCreate({ supportedLanguageCodes });

  const generateDesignerNodeForImport = (
    startNode: DesignerNodeData,
    options: GenerateDesignerNodeForImportOptions,
  ) => {
    const { dereferencedNodesByOriginalId } = options;

    if (dereferencedNodesByOriginalId) {
      for (const [originalId, node] of dereferencedNodesByOriginalId.entries()) {
        dereferencedNodesByOriginalId.set(originalId, node);
      }
    }

    type VariableEntry = { nodeId: number; name: string; config: VariableConfig };
    const variableEntries: VariableEntry[] = [];

    traverseNodeSubtree(startNode, (node) => {
      if (typeof node.config.variables !== "object") return;

      for (const [name, config] of Object.entries(node.config.variables as Record<string, VariableConfig>)) {
        variableEntries.push({ nodeId: node.id, name, config });
      }
    });

    const processReferenceVariableConfigs = (nodeConfig: DesignerNodeData["config"]) => {
      if (typeof nodeConfig.variables !== "object") {
        return nodeConfig;
      }

      const variables = Object.fromEntries(
        Object.entries(nodeConfig.variables).map(([name, config]) => {
          if (!isDirectReferenceVariable(config)) {
            return [name, config];
          }

          const ref = decomposeReferenceVariable(config);
          const referencedEntry = variableEntries.find((e) => e.nodeId === ref.nodeId && e.name === ref.name);
          if (!referencedEntry) {
            if (hasNewNodeId(ref.nodeId)) {
              return [name, composeReferenceVariable(getNewNodeId(ref.nodeId), ref.name)];
            }
            // TODO: dereference on copy because copied json is not used on within module paste
            throw new Error(`Reference variable ${JSON.stringify(config)} references node outside of scope.`);
          }

          return [name, composeReferenceVariable(getNewNodeId(ref.nodeId), ref.name)];
        }),
      );

      return {
        ...nodeConfig,
        variables,
      };
    };

    const processTriggers = (triggers?: TriggerDesign[]) => {
      if (!triggers || triggers.length === 0) return triggers;

      const processConfigRefs = <T extends Record<string, unknown>>(config: T): T => {
        const withRefs =
          "refs" in config && config.refs
            ? ({
                ...config,
                refs: Object.fromEntries(
                  Object.entries(config.refs).map(([name, idOrIds]) => [
                    name,
                    Array.isArray(idOrIds) ? idOrIds.map(getNewNodeId) : idOrIds ? getNewNodeId(idOrIds) : idOrIds,
                  ]),
                ),
              } as T)
            : config;

        return repointNodeValueRefs(withRefs, getNewNodeId);
      };

      const processCondition = ({
        id: _,
        nodeId,
        children,
        config,
        ...condition
      }: ConditionDesign): ConditionDesign => ({
        ...condition,
        id: getNextId(),
        config: processConfigRefs(config),
        nodeId: nodeId ? getNewNodeId(nodeId) : undefined,
        children: Object.fromEntries(
          Object.entries(children).map(([name, childOrChilds]) => [
            name,
            Array.isArray(childOrChilds)
              ? childOrChilds.map(processCondition)
              : childOrChilds
                ? processCondition(childOrChilds)
                : undefined,
          ]),
        ),
      });

      const processEffect = ({ id: _, nodeId, targetNodeId, config, ...effect }: EffectDesign): EffectDesign => ({
        ...effect,
        id: getNextId(),
        nodeId: getNewNodeId(nodeId),
        targetNodeId: targetNodeId ? getNewNodeId(targetNodeId) : undefined,
        config: processConfigRefs(config),
      });

      const processTriggerCondition = (where?: TriggerConditionBuild) =>
        where ? { ...where, config: processConfigRefs(where.config) } : undefined;

      return triggers.map(
        (trigger): TriggerDesign => ({
          ...trigger,
          id: getNextId(),
          cases: trigger.cases.map((triggerCase) => ({
            ...triggerCase,
            where: processTriggerCondition(triggerCase.where),
          })),
          handlers: trigger.handlers.map((handler) => ({
            ...handler,
            id: getNextId(),
            cases: handler.cases?.map((handlerCase) => ({
              ...handlerCase,
              where: processTriggerCondition(handlerCase.where),
            })),
            responses: handler.responses.map(
              ({ id: _, condition, effects, ...response }): ResponseDesign => ({
                ...response,
                id: getNextId(),
                condition: condition ? processCondition(condition) : undefined,
                effects: effects.map(processEffect),
              }),
            ),
          })),
        }),
      );
    };

    const processNodeReferences = (nodeConfig: DesignerNodeData["config"]) => {
      if (typeof nodeConfig.nodeReferences !== "object") {
        return nodeConfig;
      }

      return {
        ...nodeConfig,
        nodeReferences: Object.fromEntries(
          Object.entries(nodeConfig.nodeReferences).map(([name, idOrIds]) => [
            name,
            Array.isArray(idOrIds) ? idOrIds.map(getNewNodeId) : idOrIds ? getNewNodeId(idOrIds) : idOrIds,
          ]),
        ),
      };
    };

    return transformNodeSubtree<DesignerNodeData, DesignerNodeData>(
      startNode,
      ({ id: originalId, config, triggers, ...node }) => ({
        ...node,
        id: getNewNodeId(originalId),
        config: repointNodeValueRefs(
          processNodeReferences(processReferenceVariableConfigs(stripUnsupportedTranslations(config))),
          getNewNodeId,
        ),
        triggers: processTriggers(triggers),
      }),
    );
  };

  return generateDesignerNodeForImport;
};

export type GenerateDesignerNodeForImport = ReturnType<typeof generateDesignerNodeForImportCreate>;
